// M130 — Publish Queue: centrale goedkeuringslijst voor content_proposals.
// Ghost Mode: goedkeuren legt alleen besluit vast, geen externe publicatie.
// M135-2 — before/after tabel voor seo_page_edit voorstellen.
// M135-3 — Execute-knop blokkeert op is_test_config integraties.
// M135-8 (root) — portal-based modals (voorkomt transform-ancestor bug),
//                 strict interne-links rendering (geen lege <a>-elementen),
//                 flex-column met interne body-scroll.
import { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import api from "@/lib/api";
import { Check, X, Trash2, Eye, Loader2, AlertCircle, AlertTriangle, Send, Edit3 } from "lucide-react";

const TABS = [
  { key: "concept", label: "Concept" },
  { key: "goedgekeurd", label: "Goedgekeurd" },
  { key: "afgewezen", label: "Afgewezen" },
  { key: "ingepland", label: "Ingepland" },
];

const PLATFORM_LABEL = {
  facebook: "Facebook", tiktok: "TikTok", meta: "Meta",
  google: "Google Ads", linkedin: "LinkedIn", instagram: "Instagram",
  seo: "SEO", ads_action: "Google Ads-mutatie",
};

// M135-8 — Portal-wrapper. Rendert kinderen naar document.body zodat
// geen ancestor met CSS-transform de `position: fixed`-context kaapt.
function Modal({ children, testid, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    // Voorkom body-scroll terwijl modal open is.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          data-testid={testid}
          onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      {children}
    </div>,
    document.body,
  );
}

// M135-8 — flex-column card met interne body-scroll (header/footer vast).
function ModalCard({ children, maxWidth = "max-w-3xl" }) {
  return (
    <div className={`bg-bg-panel border border-border-hairline rounded-card w-full ${maxWidth}`}
          style={{ maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
      {children}
    </div>
  );
}

function ExecutePreviewLine({ proposalId }) {
  const [line, setLine] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/publish-queue/${proposalId}/execute-preview`);
        if (!cancelled) setLine(data.confirm_line_nl || null);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [proposalId]);
  if (!line) return null;
  return (
    <div className="mb-3 rounded border border-warn-amber/40 bg-warn-amber/5 px-3 py-2 text-[12px] text-warn-amber" data-testid="pq-execute-preview">
      {line}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("nl-NL",
      { day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function _isTypedBlogPost(p) {
  const src = (p?.source || "").toLowerCase();
  const et = (p?.execution_type || p?.payload?.execution_type || "").toLowerCase();
  return (p?.platform === "seo")
    && (et === "seo_blog_post"
        || src === "weekly_seo_blogpost"
        || src === "seo_blog_from_signal"
        || src === "weekly_blogpost");
}

function _isSeoPageEdit(p) {
  const et = (p?.execution_type || p?.payload?.execution_type || "").toLowerCase();
  return (p?.platform === "seo") && et === "seo_page_edit";
}

function _buildEditDraft(p) {
  const pl = p?.payload || {};
  const pickFirst = (...vals) => vals.find((v) => v != null && v !== "") ?? "";
  return {
    title: pickFirst(pl.title, pl.title_nl, pl.h1_nl, pl.h1),
    meta_description: pickFirst(pl.meta_description, pl.meta_description_nl, pl.meta),
    slug: pl.slug || "",
    sections: (pl.sections || []).map((s) => ({
      heading_nl: s.heading_nl || s.h2 || "",
      body_nl: s.body_nl || s.body || "",
    })),
    images: (pl.images || []).map((im) => ({
      role: im.role || "",
      url: im.url || "",
      alt_nl: im.alt_nl || im.alt || "",
      broken: !!(im.url && im.url.length < 200
                 && String(im.url).toLowerCase().includes("truncated")),
    })),
  };
}

// M135-2 — before/after-tabel voor seo_page_edit voorstellen.
// Rijen: Titel, Meta-omschrijving standaard; H1 en Intro-alinea alleen
// als het voorstel ze bevat (progressive disclosure).
function BeforeAfterTable({ payload }) {
  const p = payload || {};
  const rows = [
    { label: "Titel", before: p.title_before, after: p.chosen_title
                                                 || (p.title_alternatives || [])[0]
                                                 || p.page_title_nl
                                                 || p.title },
    { label: "Meta-omschrijving", before: p.meta_before,
      after: p.chosen_meta_description
              || (p.meta_description_alternatives || [])[0]
              || p.meta_description_nl
              || p.meta_description },
  ];
  if (p.h1_nl || p.h1_before) {
    rows.push({ label: "H1", before: p.h1_before, after: p.h1_nl });
  }
  if (p.intro_paragraph_nl || p.intro_paragraph_before) {
    rows.push({ label: "Intro-alinea",
                before: p.intro_paragraph_before,
                after: p.intro_paragraph_nl });
  }
  return (
    <div className="rounded border border-border-hairline overflow-hidden" data-testid="pq-before-after-table">
      <table className="w-full text-[12px]">
        <thead className="bg-bg-panel-raised">
          <tr>
            <th className="text-left px-3 py-2 text-[10px] text-text-muted w-32">Veld</th>
            <th className="text-left px-3 py-2 text-[10px] text-text-muted">Huidig</th>
            <th className="text-left px-3 py-2 text-[10px] text-accent-teal">Voorgesteld</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border-hairline" data-testid={`pq-before-after-row-${r.label.toLowerCase()}`}>
              <td className="px-3 py-2 text-text-muted">{r.label}</td>
              <td className="px-3 py-2 text-text-primary whitespace-pre-wrap">
                {r.before || <span className="text-text-subtle italic">— nog niet bekend</span>}
              </td>
              <td className="px-3 py-2 text-accent-teal whitespace-pre-wrap">
                {r.after || <span className="text-text-subtle italic">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// M135-8 — strict interne-links rendering. Skipt items zonder bruikbare
// url of label. Voorkomt lege `<a>`-elementen in de UI.
function InternalLinksList({ links }) {
  const arr = Array.isArray(links) ? links : [];
  const clean = arr
    .map((l) => {
      if (!l) return null;
      if (typeof l === "string") {
        const s = l.trim();
        if (!s.startsWith("http")) return null;
        return { label: s, url: s };
      }
      if (typeof l !== "object") return null;
      const url = (l.url || l.href || l.link || "").toString().trim();
      const label = (l.label || l.anchor || l.text || l.anchor_nl
                        || l.from_page_nl || "").toString().trim();
      if (!url || !label) return null;
      if (!(url.startsWith("http") || url.startsWith("/"))) return null;
      return { label, url };
    })
    .filter(Boolean);
  if (clean.length === 0) return null;
  return (
    <div data-testid="pq-typed-blog-internal-links">
      <div className="text-[10px] text-text-muted mb-1">
        Interne links
      </div>
      <ul className="text-[12px] space-y-1">
        {clean.map((l, i) => (
          <li key={i} data-testid={`pq-typed-blog-internal-link-${i}`}>
            <a href={l.url} target="_blank" rel="noreferrer"
                className="text-accent-teal hover:underline">
              {l.label}
            </a>
            <span className="text-text-subtle text-[10px] ml-1">
              — {l.url}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PublishQueuePage() {
  const [tab, setTab] = useState("concept");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [detail, setDetail] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [executeFor, setExecuteFor] = useState(null);
  const [execResult, setExecResult] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [editFor, setEditFor] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [busy, setBusy] = useState({});
  const [integrations, setIntegrations] = useState([]);
  const [highlightId, setHighlightId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // M135-3 — fetch integraties voor capability-check op Execute-knop.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/integrations/website");
        setIntegrations(data.items || []);
      } catch {}
    })();
  }, []);

  const integrationByPlatform = useMemo(() => {
    const m = {};
    for (const it of integrations) m[it.platform] = it;
    return m;
  }, [integrations]);

  // Deeplink `?open=<id>&edit=<true|false>`
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const openId = p.get("open") || p.get("highlight");
    const editMode = p.get("edit") === "true";
    if (!openId) return;
    setHighlightId(openId);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/publish-queue/${openId}`);
        if (cancelled) return;
        if (editMode) {
          setEditFor(data);
          setEditDraft(_buildEditDraft(data));
        } else {
          setDetail(data);
        }
      } catch (e) {
        setErr(e?.response?.data?.detail
          || "Concept kon niet worden geopend (mogelijk verwijderd).");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const load = async (which = tab) => {
    setErr(null);
    try {
      const { data } = await api.get(`/publish-queue?status=${which}`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Publish Queue niet beschikbaar.");
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const items = data?.items || [];
  const counts = data?.counts || {};

  const doPatch = async (id, status, reason_nl) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.patch(`/publish-queue/${id}`, { status, reason_nl });
      await load(tab);
      setRejectFor(null); setRejectReason("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Actie is niet gelukt.");
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const doExecute = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const { data } = await api.post(`/publish-queue/${id}/execute`,
        { confirmed: true, dry_run: dryRun });
      setExecResult(data);
      await load(tab);
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      setExecResult({ ok: false, reason_nl: d.reason_nl || d.detail
        || "Uitvoering is niet gelukt.", raw: d });
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  // M135-9 — server-side capability is bron van waarheid. Voor rows
  // zonder `capability` (bv. tabs anders dan goedgekeurd) valt UI terug
  // op eenvoudige heuristiek. Voor goedgekeurd blijkt hier ADVIES-modus,
  // parked-platform, ontbrekende koppeling, testconfig en drift.
  const capabilityFor = (it) => {
    const cap = it.capability;
    if (cap && typeof cap === "object") {
      const supported = !!cap.can_execute;
      const badge_label = supported ? "Ondersteund" : "Niet-uitvoerbaar";
      const badge_cls = supported
        ? "text-pos-green border-pos-green/40 bg-pos-green/10"
        : "text-warn-amber border-warn-amber/40 bg-warn-amber/10";
      return { supported, badge_label, badge_cls,
                reason_nl: cap.reason_nl,
                checks: cap.checks || {} };
    }
    // Fallback (geen server-capability geleverd) — behoud conservatief
    // gedrag: alleen Ads-actie en SEO met bekende integratie tonen we
    // als uitvoerbaar; anders "Niet-uitvoerbaar".
    return { supported: false,
              reason_nl: "Uitvoerbaarheid nog onbekend.",
              badge_label: "Niet-uitvoerbaar",
              badge_cls: "text-warn-amber border-warn-amber/40 bg-warn-amber/10",
              checks: {} };
  };

  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-display text-text-primary tracking-tight" data-testid="pq-page-title">
          Publish Queue
        </h1>
        <p className="text-xs text-text-muted mt-1">
          CONTENT-VOORSTELLEN · ADVIESMODUS · GEEN EXTERNE PUBLICATIE
        </p>
      </div>

      <div className="flex gap-2 mb-4 border-b border-border-hairline" data-testid="pq-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`pq-tab-${t.key}`}
                  className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors flex items-center gap-2 ${tab === t.key ? "border-accent-teal text-accent-teal" : "border-transparent text-text-muted hover:text-text-primary"}`}>
            {t.label}
            {counts[t.key] > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === t.key ? "bg-accent-teal/20 text-accent-teal" : "bg-bg-panel text-text-muted"}`}>
                {counts[t.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {err && (
        <div className="mb-4 rounded-md border border-warn-amber/40 bg-warn-amber/5 px-4 py-2 text-xs text-warn-amber flex items-center gap-2" data-testid="pq-error">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      {!data ? (
        <div className="text-text-muted text-sm flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Voorstellen laden…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-card border border-border-hairline border-dashed bg-bg-panel/60 p-8 text-center text-sm text-text-subtle italic" data-testid="pq-empty">
          Geen voorstellen in dit tabblad.
        </div>
      ) : (
        <div className="space-y-2" data-testid="pq-list">
          {items.map((it) => {
            const isBusy = busy[it.id];
            const cap = capabilityFor(it);
            const isHi = highlightId && it.id === highlightId;
            return (
              <div key={it.id} data-testid={`pq-row-${it.id}`}
                    className={`rounded-md border ${isHi ? "border-accent-teal/60 bg-accent-teal/5 ring-1 ring-accent-teal/30" : "border-border-hairline bg-bg-panel"} p-4 flex flex-col md:flex-row md:items-center gap-3`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] px-2 py-0.5 rounded bg-bg-panel-raised text-text-muted border border-border-hairline">
                      {PLATFORM_LABEL[it.platform] || it.platform || "Overig"}
                    </span>
                    {it.execution_type && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded border border-border-hairline text-text-subtle"
                             data-testid={`pq-row-exectype-${it.id}`}>
                        {it.execution_type === "seo_page_edit"
                          ? "Pagina-aanpassing"
                          : it.execution_type === "seo_blog_post"
                            ? "Nieuwe blog"
                            : it.execution_type}
                      </span>
                    )}
                    {it.tab === "goedgekeurd" && (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded border ${cap.badge_cls}`}
                             data-testid={`pq-row-capability-${it.id}`}>
                        {cap.badge_label}
                      </span>
                    )}
                    <span className="text-[10px] text-text-subtle">
                      {fmtDate(it.created_at)}
                    </span>
                    {it.source === "social_suite" && (
                      <span className="text-[9px] text-accent-teal">
                        social suite
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-text-primary font-semibold mb-1 truncate">
                    {it.topic || "Zonder onderwerp"}
                  </div>
                  <div className="text-xs text-text-muted line-clamp-2">
                    {it.preview_nl || "(geen preview)"}
                  </div>
                  {it.reject_reason_nl && (
                    <div className="mt-1 text-[11px] text-warn-amber italic">
                      Reden: {it.reject_reason_nl}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setDetail(it)} data-testid={`pq-view-${it.id}`}
                          className="text-[11px] px-2 py-1 rounded border border-border-hairline hover:border-accent-teal/40 text-text-primary flex items-center gap-1">
                    <Eye className="w-3 h-3" /> Bekijken
                  </button>
                  {it.tab !== "goedgekeurd" && (
                    <button onClick={() => doPatch(it.id, "approved")} disabled={isBusy} data-testid={`pq-approve-${it.id}`}
                            title="Publiceren wordt in een latere fase mogelijk"
                            className="text-[11px] px-2 py-1 rounded border border-pos-green/40 hover:bg-pos-green/10 text-pos-green flex items-center gap-1 disabled:opacity-50">
                      <Check className="w-3 h-3" /> Goedkeuren
                    </button>
                  )}
                  {it.tab !== "afgewezen" && (
                    <button onClick={() => setRejectFor(it)} disabled={isBusy} data-testid={`pq-reject-${it.id}`}
                            className="text-[11px] px-2 py-1 rounded border border-warn-amber/40 hover:bg-warn-amber/10 text-warn-amber flex items-center gap-1 disabled:opacity-50">
                      <X className="w-3 h-3" /> Afwijzen
                    </button>
                  )}
                  {it.tab === "goedgekeurd" && it.platform !== "tiktok" && it.platform !== "facebook" && (
                    <button onClick={() => setExecuteFor(it)}
                            disabled={isBusy || !cap.supported}
                            data-testid={`pq-execute-${it.id}`}
                            title={cap.supported
                                    ? "Uitvoeren gebeurt alleen na jouw expliciete bevestiging."
                                    : cap.reason_nl}
                            className={`text-[11px] px-2 py-1 rounded border flex items-center gap-1 disabled:opacity-40 ${ cap.supported ? "border-accent-teal/40 hover:bg-accent-teal/10 text-accent-teal" : "border-warn-amber/40 text-warn-amber cursor-not-allowed"}`}>
                      <Send className="w-3 h-3" /> Uitvoeren
                    </button>
                  )}
                  <button onClick={() => { if (window.confirm("Voorstel definitief verwijderen?")) doPatch(it.id, "deleted"); }} disabled={isBusy} data-testid={`pq-delete-${it.id}`}
                          className="text-[11px] px-2 py-1 rounded border border-border-hairline hover:border-neg-red/40 text-text-muted hover:text-neg-red flex items-center gap-1 disabled:opacity-50">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data?.note_nl && (
        <p className="mt-4 text-[11px] text-text-subtle italic" data-testid="pq-note">{data.note_nl}</p>
      )}

      {/* -------- Detail modal (portal + interne scroll) -------- */}
      {detail && (
        <Modal testid="pq-detail-modal" onClose={() => setDetail(null)}>
          <ModalCard>
            <div className="p-5 border-b border-border-hairline shrink-0"
                  style={{ flex: "0 0 auto" }}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] text-text-muted">
                  {PLATFORM_LABEL[detail.platform] || detail.platform} · {fmtDate(detail.created_at)}
                  {detail.status === "superseded" && (
                    <span className="ml-2 pill-outline text-warn-amber">Verouderd</span>
                  )}
                </div>
                {_isTypedBlogPost(detail) && (
                  <button
                    data-testid="pq-detail-edit"
                    onClick={() => { setEditFor(detail); setEditDraft(_buildEditDraft(detail)); setDetail(null); }}
                    className="text-[11px] px-2 py-1 rounded border border-accent-teal/40 bg-accent-teal/10 text-accent-teal inline-flex items-center gap-1">
                    <Edit3 className="w-3 h-3" /> Bewerken
                  </button>
                )}
              </div>
            </div>
            <div className="p-5 overflow-y-auto"
                  style={{ flex: "1 1 auto", minHeight: 0 }}>
              {_isSeoPageEdit(detail) ? (
                <div data-testid="pq-seo-page-edit-preview">
                  <div className="text-sm font-semibold text-text-primary mb-1">
                    {detail.topic || "Pagina-aanpassing"}
                  </div>
                  {detail.payload?.target_page_url && (
                    <div className="text-[11px] text-text-muted mb-3 break-all">
                      <span className="font-mono">{detail.payload.target_page_url}</span>
                    </div>
                  )}
                  <BeforeAfterTable payload={detail.payload || {}} />
                  {detail.payload?.why_nl && (
                    <div className="mt-3 text-[12px] text-text-muted italic">
                      <span className="text-[10px]">Waarom: </span>
                      {detail.payload.why_nl}
                    </div>
                  )}
                </div>
              ) : _isTypedBlogPost(detail) ? (
                (() => {
                  const pl = detail.payload || {};
                  const sections = pl.sections || [];
                  const images = pl.images || [];
                  return (
                    <div className="space-y-3" data-testid="pq-typed-blog-preview">
                      <div>
                        <h1 className="text-lg font-semibold text-text-primary">
                          {pl.title || detail.topic || "Zonder titel"}
                        </h1>
                        {pl.meta_description && (
                          <div className="text-[12px] text-text-muted italic mt-1">
                            {pl.meta_description}
                          </div>
                        )}
                        {pl.slug && (
                          <div className="text-[10px] text-text-muted mt-1">/{pl.slug}</div>
                        )}
                      </div>
                      {images.length > 0 && (
                        <div className="grid grid-cols-3 gap-2" data-testid="pq-typed-blog-images">
                          {images.map((im, i) => (
                            <figure key={i} className="rounded overflow-hidden border border-border-hairline">
                              {im.url && !im.broken ? (
                                <img src={im.url} alt={im.alt_nl || ""}
                                      className="w-full h-24 object-cover bg-bg-canvas" />
                              ) : (
                                <div className="w-full h-24 bg-bg-canvas flex items-center justify-center text-[10px] text-text-muted">
                                  {im.broken ? "Bron beschadigd" : "Geen afbeelding"}
                                </div>
                              )}
                              <figcaption className="p-1 text-[9px] text-text-muted flex justify-between">
                                <span>{im.role || "afbeelding"}</span>
                                <span className="italic normal-case text-[9px]">{im.alt_nl?.slice(0, 40) || "—"}</span>
                              </figcaption>
                            </figure>
                          ))}
                        </div>
                      )}
                      <div className="space-y-3">
                        {sections.length > 0 ? sections.map((s, i) => (
                          <div key={i} data-testid={`pq-typed-blog-section-${i}`}>
                            <h2 className="text-sm font-semibold text-text-primary mb-1">
                              {s.heading_nl || s.h2 || `Sectie ${i + 1}`}
                            </h2>
                            <div className="text-[13px] text-text-primary leading-relaxed whitespace-pre-wrap">
                              {s.body_nl || s.body || ""}
                            </div>
                          </div>
                        )) : (
                          <div className="text-[12px] text-text-muted italic">
                            Deze blog heeft nog geen ingedeelde secties.
                          </div>
                        )}
                      </div>
                      <InternalLinksList links={pl.internal_links} />
                    </div>
                  );
                })()
              ) : (
                <>
                  <div className="text-sm font-semibold text-text-primary mb-3">
                    {detail.topic || "Zonder onderwerp"}
                  </div>
                  {Object.entries(detail.payload || {}).map(([k, v]) => (
                    <div key={k} className="text-[11px] mb-2">
                      <div className="text-text-muted text-[9px]">{k}</div>
                      <div className="text-text-primary whitespace-pre-wrap">
                        {Array.isArray(v) ? v.join("  ") : (typeof v === "object" ? JSON.stringify(v, null, 2) : String(v))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="p-4 border-t border-border-hairline shrink-0"
                  style={{ flex: "0 0 auto" }}>
              <button onClick={() => setDetail(null)} data-testid="pq-detail-close"
                      className="text-[11px] px-3 py-1 rounded border border-border-hairline text-text-primary">
                Sluiten
              </button>
            </div>
          </ModalCard>
        </Modal>
      )}

      {/* -------- Edit modal (portal + interne scroll) -------- */}
      {editFor && editDraft && (
        <Modal testid="pq-edit-modal" onClose={() => { setEditFor(null); setEditDraft(null); }}>
          <ModalCard>
            <div className="p-5 border-b border-border-hairline shrink-0"
                  style={{ flex: "0 0 auto" }}>
              <div className="text-[10px] text-text-muted mb-1">
                Concept bewerken — versie {editFor.payload_version || 1}
              </div>
              <div className="text-sm font-semibold text-text-primary">
                {editFor.topic || "Zonder onderwerp"}
              </div>
            </div>
            <div className="p-5 overflow-y-auto"
                  style={{ flex: "1 1 auto", minHeight: 0 }}>
              <label className="block text-[11px] mb-2">
                <div className="text-text-muted mb-0.5">Titel</div>
                <input type="text" value={editDraft.title}
                        onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                        data-testid="pq-edit-title"
                        className="w-full bg-bg-panel-raised border border-border-hairline rounded px-2 py-1 text-text-primary" />
              </label>
              <label className="block text-[11px] mb-2">
                <div className="text-text-muted mb-0.5">Meta-omschrijving</div>
                <textarea value={editDraft.meta_description}
                          onChange={(e) => setEditDraft({ ...editDraft, meta_description: e.target.value })}
                          data-testid="pq-edit-meta"
                          className="w-full h-16 bg-bg-panel-raised border border-border-hairline rounded px-2 py-1 text-text-primary" />
              </label>
              <label className="block text-[11px] mb-3">
                <div className="text-text-muted mb-0.5">Slug</div>
                <input type="text" value={editDraft.slug}
                        onChange={(e) => setEditDraft({ ...editDraft, slug: e.target.value })}
                        data-testid="pq-edit-slug"
                        className="w-full bg-bg-panel-raised border border-border-hairline rounded px-2 py-1 text-text-primary" />
              </label>
              {editDraft.images.length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] text-text-muted mb-1">
                    Afbeeldingen — alt-tekst per afbeelding
                  </div>
                  {editDraft.images.map((im, i) => (
                    <div key={i} className="mb-2 flex gap-2 items-start">
                      {im.url && !im.broken && (
                        <img src={im.url} alt={im.alt_nl || ""}
                              className="w-16 h-12 object-cover rounded border border-border-hairline bg-bg-canvas" />
                      )}
                      {(!im.url || im.broken) && (
                        <div className="w-16 h-12 rounded border border-border-hairline bg-bg-canvas flex items-center justify-center text-[9px] text-text-muted">
                          {im.broken ? "beschadigd" : "geen"}
                        </div>
                      )}
                      <input type="text" value={im.alt_nl}
                              data-testid={`pq-edit-alt-${i}`}
                              onChange={(e) => {
                                const imgs = [...editDraft.images];
                                imgs[i] = { ...imgs[i], alt_nl: e.target.value };
                                setEditDraft({ ...editDraft, images: imgs });
                              }}
                              placeholder={`Alt-tekst voor ${im.role || "afbeelding"}`}
                              className="flex-1 bg-bg-panel-raised border border-border-hairline rounded px-2 py-1 text-[11px] text-text-primary" />
                    </div>
                  ))}
                </div>
              )}
              {editDraft.sections.map((s, i) => (
                <div key={i} className="mb-3 border-t border-border-hairline pt-2">
                  <div className="text-[10px] text-text-muted mb-1">
                    Sectie {i + 1} — {s.heading_nl}
                  </div>
                  <textarea value={s.body_nl}
                            data-testid={`pq-edit-section-${i}`}
                            onChange={(e) => {
                              const secs = [...editDraft.sections];
                              secs[i] = { ...secs[i], body_nl: e.target.value };
                              setEditDraft({ ...editDraft, sections: secs });
                            }}
                            className="w-full h-24 bg-bg-panel-raised border border-border-hairline rounded px-2 py-1 text-[12px] text-text-primary" />
                </div>
              ))}
              <div className="text-[10px] text-text-muted italic mt-2">
                Bij opslaan wordt de vorige versie bewaard en, indien het
                concept al goedgekeurd was, keert de status terug naar
                <span className="font-mono"> Concept</span>.
              </div>
            </div>
            <div className="p-4 border-t border-border-hairline flex justify-end gap-2 shrink-0"
                  style={{ flex: "0 0 auto" }}>
              <button onClick={() => { setEditFor(null); setEditDraft(null); }}
                      data-testid="pq-edit-cancel"
                      className="text-[11px] px-3 py-1 rounded border border-border-hairline text-text-muted">
                Annuleren
              </button>
              <button data-testid="pq-edit-save"
                      onClick={async () => {
                        setBusy((b) => ({ ...b, [editFor.id]: true }));
                        try {
                          const orig = _buildEditDraft(editFor);
                          const changes = {};
                          if (editDraft.title !== orig.title) changes.title = editDraft.title;
                          if (editDraft.meta_description !== orig.meta_description) changes.meta_description = editDraft.meta_description;
                          if (editDraft.slug !== orig.slug) changes.slug = editDraft.slug;
                          const secsChanged = editDraft.sections.some((s, i) =>
                            !orig.sections[i]
                            || s.heading_nl !== orig.sections[i].heading_nl
                            || s.body_nl !== orig.sections[i].body_nl);
                          if (secsChanged) changes.sections = editDraft.sections;
                          const altsChanged = editDraft.images.some((im, i) =>
                            !orig.images[i] || im.alt_nl !== orig.images[i].alt_nl);
                          if (altsChanged) {
                            changes.images = editDraft.images.map((im) => ({
                              role: im.role, alt_nl: im.alt_nl,
                              broken: !!im.broken,
                              url: im.broken ? im.url : undefined,
                            }));
                          }
                          if (Object.keys(changes).length === 0) {
                            setEditFor(null); setEditDraft(null);
                            return;
                          }
                          await api.patch(`/publish-queue/${editFor.id}/payload`, {
                            payload_updates: changes,
                            edit_reason_nl: "Handmatige bewerking via Publish Queue",
                          });
                          setEditFor(null); setEditDraft(null);
                          await load(tab);
                        } catch (e) {
                          setErr(e?.response?.data?.detail
                            || "Opslaan mislukt.");
                        } finally {
                          setBusy((b) => ({ ...b, [editFor.id]: false }));
                        }
                      }}
                      className="text-[11px] px-3 py-1 rounded border border-accent-teal/40 bg-accent-teal/10 text-accent-teal">
                Opslaan
              </button>
            </div>
          </ModalCard>
        </Modal>
      )}

      {rejectFor && (
        <Modal testid="pq-reject-modal" onClose={() => { setRejectFor(null); setRejectReason(""); }}>
          <ModalCard maxWidth="max-w-md">
            <div className="p-5">
              <div className="text-sm font-semibold text-text-primary mb-3">Voorstel afwijzen</div>
              <div className="text-xs text-text-muted mb-2">Reden (optioneel):</div>
              <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                        data-testid="pq-reject-reason"
                        className="w-full h-24 text-sm bg-bg-panel-raised border border-border-hairline rounded px-2 py-1.5 text-text-primary" />
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={() => { setRejectFor(null); setRejectReason(""); }}
                        className="text-[11px] px-3 py-1 rounded border border-border-hairline text-text-muted">
                  Annuleren
                </button>
                <button onClick={() => doPatch(rejectFor.id, "rejected", rejectReason)} data-testid="pq-reject-confirm"
                        className="text-[11px] px-3 py-1 rounded border border-warn-amber/40 bg-warn-amber/10 text-warn-amber">
                  Afwijzen bevestigen
                </button>
              </div>
            </div>
          </ModalCard>
        </Modal>
      )}

      {executeFor && (() => {
        const cap = capabilityFor(executeFor);
        return (
        <Modal testid="pq-execute-modal" onClose={() => setExecuteFor(null)}>
          <ModalCard maxWidth="max-w-md">
            <div className="p-5">
              <div className="text-sm font-semibold text-text-primary mb-2">Voorstel uitvoeren</div>
              <div className="text-xs text-text-muted mb-3">
                Platform: <span className="text-text-primary">{executeFor.platform}</span>
                {executeFor.target_platform && (
                  <> · Bestemming: <span className="text-text-primary">{executeFor.target_platform}</span></>
                )}<br />
                Onderwerp: {executeFor.topic || "—"}
              </div>
              <div className={`mb-3 rounded border px-3 py-2 text-[11px] flex items-start gap-2 ${cap.badge_cls}`}
                    data-testid="pq-execute-capability">
                {cap.supported
                  ? <Check className="w-3.5 h-3.5 mt-0.5" />
                  : <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />}
                <span>{cap.supported ? "Ondersteund — je kunt valideren of live uitvoeren."
                                      : cap.reason_nl}</span>
              </div>
              <ExecutePreviewLine proposalId={executeFor.id} />
              <label className="flex items-start gap-2 mb-3 cursor-pointer select-none rounded border border-accent-teal/30 bg-accent-teal/5 p-3">
                <input type="checkbox"
                       checked={dryRun}
                       onChange={(e) => setDryRun(e.target.checked)}
                       data-testid="pq-execute-dryrun"
                       className="mt-0.5 accent-accent-teal" />
                <span className="text-[12px] text-text-primary">
                  <span className="font-semibold">Alleen valideren</span>
                  <span className="text-text-muted"> (geen echte wijziging)</span>
                  <div className="text-[11px] text-text-muted mt-1 italic">
                    Aanbevolen. Google Ads valideert dan de mutatie server-side
                    met <span className="font-mono">validate_only=true</span>,
                    maar past niets aan.
                  </div>
                </span>
              </label>
              <div className="text-[11px] text-text-muted mb-3 italic">
                {dryRun
                  ? "Validatie-modus: er wordt niets extern gewijzigd."
                  : "Live-modus: dit voorstel wordt echt uitgevoerd. Klik alleen als je zeker bent."}
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setExecuteFor(null)} data-testid="pq-execute-cancel"
                         className="text-[11px] px-3 py-1 rounded border border-border-hairline text-text-muted">Annuleren</button>
                <button onClick={() => { doExecute(executeFor.id); setExecuteFor(null); }}
                         disabled={!cap.supported}
                         data-testid="pq-execute-confirm"
                         className={`text-[11px] px-3 py-1 rounded border disabled:opacity-40 ${dryRun ? "border-accent-teal/40 bg-accent-teal/10 text-accent-teal" : "border-warn-amber/60 bg-warn-amber/10 text-warn-amber"}`}>
                  {dryRun ? "Valideren" : "Live uitvoeren"}
                </button>
              </div>
            </div>
          </ModalCard>
        </Modal>
        );
      })()}
      {execResult && (
        <Modal testid="pq-exec-result-modal" onClose={() => setExecResult(null)}>
          <ModalCard maxWidth="max-w-md">
            <div className="p-5">
              <div className={`text-sm font-semibold mb-2 ${execResult.ok ? "text-pos-green" : "text-warn-amber"}`}>
                {(() => {
                  const r = execResult.result || {};
                  if (!execResult.ok) return "Uitvoering mislukt";
                  if (r.status_at_platform === "validated"
                      || r.validate_only) return "Validatie geslaagd — geen wijziging doorgevoerd";
                  return "Uitvoering geslaagd";
                })()}
              </div>
              {execResult.reason_nl && (
                <div className="text-[12px] text-text-primary mb-2" data-testid="pq-exec-result-reason">
                  {execResult.reason_nl}
                </div>
              )}
              <pre className="text-[11px] text-text-muted whitespace-pre-wrap bg-bg-canvas rounded p-2 max-h-64 overflow-auto">
                {JSON.stringify(execResult.result || execResult.log || execResult, null, 2)}
              </pre>
              <button onClick={() => setExecResult(null)} className="mt-3 text-[11px] px-3 py-1 rounded border border-border-hairline text-text-primary">Sluiten</button>
            </div>
          </ModalCard>
        </Modal>
      )}
    </AppLayout>
  );
}
